// A forgiving name search for the in-page search boxes (Ben, 2026-10-16: "Ødegaard" was found
// but "Odegaard" wasn't). Pure.
//
// - Accents and special letters don't matter, either way: Ø→o, é→e, ü→u, ß→ss, æ→ae, ł→l…
// - Words in any order, and partial words: "kane harry", "odeg".
// - One wrong / missing / extra letter forgiven in a word of 4+ letters ("odegard", "bellingam").
// - Scored, so the best matches can come first (exact start > word start > inside > typo).

import { COUNTRY_ALIASES } from "./country-aliases.js";

// Letters that Unicode decomposition doesn't turn into plain a–z.
const SPECIAL: Record<string, string> = {
  ø: "o", æ: "ae", œ: "oe", ß: "ss", ł: "l", đ: "d", ð: "d", þ: "th", ı: "i", ħ: "h", ŧ: "t", ŀ: "l",
};

/** Lower-case, accents and special letters folded to plain letters, punctuation as spaces. */
export function foldText(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[øæœßłđðþıħŧŀ]/g, (c) => SPECIAL[c] ?? c)
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** At most one edit (substitute / insert / delete) apart? */
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

/** How well one query word matches the name's words: 3 = a word starts with it, 2 = inside a
 *  word, 1 = one typo away from a word (or its start), 0 = no match. */
function wordScore(q: string, words: readonly string[]): number {
  if (words.some((w) => w.startsWith(q))) return 3;
  if (words.some((w) => w.includes(q))) return 2;
  if (q.length >= 4 && words.some((w) => withinOneEdit(q, w) || withinOneEdit(q, w.slice(0, q.length)))) return 1;
  return 0;
}

/**
 * How well `query` matches `text` (higher = better), or 0 for no match. Every query word must
 * match some word of the text. An empty query matches nothing.
 */
export function fuzzyScore(query: string, text: string): number {
  return fuzzyScoreFolded(foldText(query), foldText(text));
}

/** `fuzzyScore` on texts already through `foldText` — for a list searched many times (the live
 *  player file: 170,000+ names, folded once instead of on every key). */
export function fuzzyScoreFolded(q: string, t: string): number {
  if (!q) return 0;
  const words = t.split(" ");
  let score = 0;
  for (const part of q.split(" ")) {
    const s = wordScore(part, words);
    if (s === 0) return 0;
    score += s;
  }
  // The whole query at the very start of the text ranks first.
  if (t.startsWith(q)) score += 10;
  return score;
}

/** Does `query` match `text` at all? */
export const fuzzyMatches = (query: string, text: string) => fuzzyScore(query, text) > 0;

/** `items` matching `query`, best first (ties keep their order). */
export function fuzzyFilter<T>(items: readonly T[], query: string, textOf: (item: T) => string): T[] {
  return items
    .map((item, i) => ({ item, i, score: fuzzyScore(query, textOf(item)) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((x) => x.item);
}

// --- other names for countries ----------------------------------------------------------------------

const FOLDED_ALIASES = COUNTRY_ALIASES.map((c) => ({
  names: c.names.map((n) => ` ${foldText(n)} `),
  aliases: c.aliases.join(" "),
}));

/** `text` plus any other names for a country it mentions ("… Netherlands" → "… Netherlands Holland
 *  Dutch Nederland"), so a search for "Holland" finds it (Ben, 2026-10-18; data/country-aliases.ts). */
export function withCountryAliases(text: string): string {
  const folded = ` ${foldText(text)} `;
  const extra = FOLDED_ALIASES.filter((c) => c.names.some((n) => folded.includes(n))).map((c) => c.aliases);
  return extra.length ? `${text} ${extra.join(" ")}` : text;
}
